import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './GiftingOccasionsBrandRefresh.module.css';
import {
  GIFTING_OCCASIONS_DEFAULT,
  GIFTING_OCCASIONS_HEADLINE_LINES_MOBILE,
  GIFTING_OCCASIONS_MOBILE_LAYOUT_MAX_WIDTH,
} from './giftingOccasionsConstants';

function OccasionCard({ title, description, imageSrc, imageAlt }) {
  return (
    <article className={styles.card} data-part="occasion-card">
      <div className={styles.cardMedia}>
        <img src={imageSrc} alt={imageAlt} className={styles.cardImage} />
      </div>
      <div className={styles.cardCopy}>
        <h3 className={styles.cardTitle}>{title}</h3>
        <p className={styles.cardDescription}>{description}</p>
      </div>
    </article>
  );
}

OccasionCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  imageSrc: PropTypes.string.isRequired,
  imageAlt: PropTypes.string.isRequired,
};

function GiftingOccasionsBrandRefresh({
  headlineLines,
  ctaText,
  ctaLink,
  ctaButtonType,
  contentSection,
  cards,
  onCtaClick,
}) {
  const [isMobileLayout, setIsMobileLayout] = useState(
    typeof window !== 'undefined'
      && window.innerWidth <= GIFTING_OCCASIONS_MOBILE_LAYOUT_MAX_WIDTH
  );

  useEffect(() => {
    const onResize = () => {
      setIsMobileLayout(window.innerWidth <= GIFTING_OCCASIONS_MOBILE_LAYOUT_MAX_WIDTH);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleCtaClick = () => {
    if (onCtaClick && ctaLink) {
      onCtaClick(ctaLink, ctaButtonType, ctaText, contentSection);
    }
  };

  const displayHeadlineLines = isMobileLayout
    ? GIFTING_OCCASIONS_HEADLINE_LINES_MOBILE
    : headlineLines;

  const cardRows = [];
  for (let i = 0; i < cards.length; i += 2) {
    cardRows.push(cards.slice(i, i + 2));
  }

  return (
    <section
      className={styles.section}
      data-region="gifting-occasions"
      aria-labelledby="gifting-occasions-heading"
    >
      <div className={styles.layout}>
        <aside className={styles.sidebar} data-part="occasions-sidebar">
          <div className={styles.sidebarSticky}>
            <h2 className={styles.headline} id="gifting-occasions-heading">
              {displayHeadlineLines.map((line, index) => (
                <span key={line} className={styles.headlineLine}>
                  {line}
                  {index < displayHeadlineLines.length - 1 ? <br /> : null}
                </span>
              ))}
            </h2>
            {!isMobileLayout ? (
              <button
                type="button"
                className={styles.cta}
                data-part="occasions-cta"
                onClick={handleCtaClick}
              >
                {ctaText}
              </button>
            ) : null}
          </div>
        </aside>

        <div className={styles.grid} data-part="occasions-grid">
          {cardRows.map(row => (
            <div key={row.map(card => card.id).join('-')} className={styles.gridRow}>
              {row.map(card => (
                <OccasionCard
                  key={card.id}
                  title={card.title}
                  description={card.description}
                  imageSrc={card.imageSrc}
                  imageAlt={card.imageAlt}
                />
              ))}
            </div>
          ))}
        </div>

        {isMobileLayout ? (
          <button
            type="button"
            className={`${styles.cta} ${styles.ctaFooter}`}
            data-part="occasions-cta"
            onClick={handleCtaClick}
          >
            {ctaText}
          </button>
        ) : null}
      </div>
    </section>
  );
}

GiftingOccasionsBrandRefresh.propTypes = {
  headlineLines: PropTypes.arrayOf(PropTypes.string),
  ctaText: PropTypes.string,
  ctaLink: PropTypes.string,
  ctaButtonType: PropTypes.string,
  contentSection: PropTypes.string,
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      imageSrc: PropTypes.string.isRequired,
      imageAlt: PropTypes.string.isRequired,
    })
  ),
  onCtaClick: PropTypes.func,
};

GiftingOccasionsBrandRefresh.defaultProps = {
  ...GIFTING_OCCASIONS_DEFAULT,
  onCtaClick: null,
};

export default GiftingOccasionsBrandRefresh;
